import { FootnoteStyleSelector } from '../enum';
import { trackFootnoteLink } from '../tracking/tracking';
import {
    closeAccordion,
    footnoteAccordion,
} from './accordion';
import {
    ContentId,
    HtmlId,
} from '../enums/elementId';
import { isStringANumber } from '../util/typeGuard';
import {
    getDiscountFootnoteNumber,
    getDiscountFootnoteNumberWithTv,
    isPlaystationPromoActive,
} from '../util/cms';
import {
    selectFnfElement,
    selectTvElement,
} from '../util/selectors';

const addAccordionOpenEvent = (footnoteLinkEl: HTMLElement, footnoteNumber: number) => {
    const accordionItems = footnoteAccordion?.getPropertyByKey('items') || [];
    const totalAccordions = accordionItems.length;

    let accordionId: string | undefined;

    // TODO: to revist the footnote navigation behavior to depend on id instead of index
    if (isPlaystationPromoActive() && footnoteNumber > totalAccordions) {
        accordionId = accordionItems.find(item => item.stdId === ContentId.PS_ACCORDION)?.stdId;
    }
    else {
        accordionId = accordionItems[footnoteNumber - 1]?.stdId;
    }

    if (!accordionId) {
        return;
    }

    footnoteLinkEl.onclick = () => {
        if (!footnoteAccordion) {
            return;
        }

        closeAccordion(footnoteAccordion);
        footnoteAccordion.toggleItemById(accordionId, true);
        trackFootnoteLink(footnoteLinkEl.innerText.slice(0, -1));
    };
};

const addOverlayScrollEvent = (footnoteLinkEl: HTMLElement, footnoteNumber: number, overlayId: HtmlId) => {
    const overlayEl = document.getElementById(overlayId);
    const targetElements = overlayEl?.getElementsByClassName(FootnoteStyleSelector.FOOTNOTE_LINK_TARGET);

    const targetEl = Array.of(...targetElements || []).find(el => {
        const targetIndex = (el as HTMLElement).innerText;

        return targetIndex && isStringANumber(targetIndex) && +targetIndex === footnoteNumber;
    });

    footnoteLinkEl.onclick = () => {
        targetEl?.scrollIntoView();
        trackFootnoteLink(getFootnoteLinkText(footnoteLinkEl));
    };
};

/**
 * Add accordion item open click event to existing footnote link html
 *
 * @param id - Wrapper ID of the footnote link element.
 * @param overlayId - Wrapper ID of the overlay container element, if the
 * footnote link is located in an overlay.
 */
export const mountFootnoteLink = (id: HtmlId, overlayId?: HtmlId) => {
    const [footnoteLinkEl] = document.getElementById(id)?.getElementsByClassName(FootnoteStyleSelector.FOOTNOTE_LINK) || [];
    const footnoteNumber = (footnoteLinkEl as HTMLElement)?.innerText;

    if (!footnoteNumber || !isStringANumber(footnoteNumber)) {
        return;
    }

    overlayId
        ? addOverlayScrollEvent(footnoteLinkEl as HTMLElement, +footnoteNumber, overlayId)
        : addAccordionOpenEvent(footnoteLinkEl as HTMLElement, +footnoteNumber);
};

/**
 * Add footnote link html to element with accordion item open click event
 *
 * @param element - The element the footnote should be appended on
 * @param footnoteNumber - The number the footnote should have
 */
export const updateFootnoteLink = (element: Element, footnoteNumber: number) => {
    if (element.getElementsByClassName(FootnoteStyleSelector.FOOTNOTE_LINK).length) {
        const footnoteSupElement = element.getElementsByClassName(FootnoteStyleSelector.FOOTNOTE_LINK)[0];
        footnoteSupElement.innerHTML = footnoteNumber.toString();
        addAccordionOpenEvent(footnoteSupElement as HTMLElement, +footnoteNumber);

        return;
    }

    const footnoteLinkEl = document.createElement('sup');
    footnoteLinkEl.classList.add(FootnoteStyleSelector.FOOTNOTE_LINK);
    footnoteLinkEl.innerHTML = footnoteNumber.toString();
    element.appendChild(footnoteLinkEl);

    addAccordionOpenEvent(footnoteLinkEl as HTMLElement, +footnoteNumber);
};

/**
 * Returns the text of the given parent element.
 *
 * @param element - The element for which the function searches the text.
 * @param searchLevels - Up to how many parents should the function search for text?
 * @returns The inner text of a parent element.
 */
export const getFootnoteLinkText = (element: HTMLElement, searchLevels = 1) => {
    let currentEl: HTMLElement | null = element.parentElement;

    for (let i = 0; i < searchLevels && currentEl; i++) {
        if (currentEl.innerText) {
            return currentEl.getElementsByClassName(FootnoteStyleSelector.FOOTNOTE_LINK).length
                ? currentEl.innerText.trim().slice(0, -1)
                : currentEl.innerText;
        }

        currentEl = currentEl.parentElement;
    }

    return '';
};

/**
 * Updates the footnote link in the Friends & Family legal text.
 */
export const updateFnfFootnoteLink = (isTv?: boolean) => {
    const fnfElement = selectFnfElement();
    const isTvHidden = selectTvElement();

    fnfElement && updateFootnoteLink(
        fnfElement as Element,
        (isTv || !isTvHidden) ? getDiscountFootnoteNumberWithTv() as number : getDiscountFootnoteNumber() as number,
    );
};
